import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactApexChart from 'react-apexcharts';
import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { useCollection } from '../../../hooks/useCollection';
import { fetchTrack } from '../../../utils/gpsTrackDb';
import {
  computeTripsStops, speedBuckets, dailySeries, driverBehavior,
  fleetStatus, deviceActivity, fleetKpis, fmtDuration,
} from '../utils/analyticsData';

const PALETTE = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#8b5cf6', '#ec4899'];
const RANGES = [
  { key: '24h', fr: '24h', ar: '24س', ms: 86400000 },
  { key: '7d', fr: '7 jours', ar: '7 أيام', ms: 7 * 86400000 },
  { key: '30d', fr: '30 jours', ar: '30 يوم', ms: 30 * 86400000 },
  { key: '90d', fr: '90 jours', ar: '90 يوم', ms: 90 * 86400000 },
];

const carLabel = (c) => `${c.brand || ''} ${c.model || ''}${c.plate ? ` (${c.plate})` : ''}`.trim() || c.id;

export default function CarAnalyticsPage({ settings = {} }) {
  const { lang } = useLanguage();
  const { isDark } = useTheme();
  const { agencyId } = useAuth();
  const isAr = lang === 'ar';
  const t = (fr, ar) => (isAr ? ar : fr);

  const { data: cars } = useCollection('cars');
  const { data: breakdowns } = useCollection('breakdowns');
  const { data: vidanges } = useCollection('vidanges');
  const { data: mokhalafat } = useCollection('mokhalafat');

  const [selectedIds, setSelectedIds] = useState([]);
  const [rangeKey, setRangeKey] = useState('7d');
  // Custom exact date+time window
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [tracksByCar, setTracksByCar] = useState({}); // id -> points[]
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [realtime, setRealtime] = useState(false);
  const dashRef = useRef(null);
  // Searchable vehicle selector
  const [vehicleQuery, setVehicleQuery] = useState('');
  const [vehicleOpen, setVehicleOpen] = useState(false);
  const selectorRef = useRef(null);

  // Default selection: ONCE, the first time cars are available. A ref guard
  // prevents the realtime cars updates from ever resetting the user's choice.
  const initSelRef = useRef(false);
  useEffect(() => {
    if (!initSelRef.current && cars.length) {
      initSelRef.current = true;
      setSelectedIds([cars[0].id]);
    }
  }, [cars.length]);

  const range = RANGES.find(r => r.key === rangeKey) || RANGES[1];
  // IMPORTANT: stabilise the time window so it only changes when the user picks a
  // new range — recomputing Date.now() every render made loadTracks change on
  // every render and caused an endless refetch/refresh loop (the bug).
  // For 'custom', use the exact start/end datetime the user entered.
  const { sinceMs, untilMs } = useMemo(() => {
    if (rangeKey === 'custom') {
      const s = customFrom ? new Date(customFrom).getTime() : (Date.now() - 7 * 86400000);
      const u = customTo ? new Date(customTo).getTime() : Date.now();
      return { sinceMs: s, untilMs: u };
    }
    return { sinceMs: Date.now() - range.ms, untilMs: Date.now() };
  }, [rangeKey, customFrom, customTo]); // eslint-disable-line

  // Key the fetch by the actual selection (not the array identity) so realtime
  // re-renders don't re-trigger a load unless the selection really changed.
  const selKey = useMemo(() => selectedIds.slice(0, 6).join(','), [selectedIds]);

  const loadTracks = useCallback(async () => {
    const ids = selKey ? selKey.split(',') : [];
    if (!ids.length) { setTracksByCar({}); return; }
    setLoadingTracks(true);
    const out = {};
    await Promise.all(ids.map(async (id) => {
      out[id] = await fetchTrack({ agencyId, vehicleId: id, sinceMs, untilMs });
    }));
    setTracksByCar(out);
    setLoadingTracks(false);
  }, [selKey, agencyId, sinceMs, untilMs]);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  // Real-time refresh: re-fetch tracks on an interval ONLY when enabled. Does
  // not reset the selection or reload the page.
  useEffect(() => {
    if (!realtime) return;
    const id = setInterval(() => { loadTracks(); }, 30000);
    return () => clearInterval(id);
  }, [realtime, loadTracks]);

  // Close the selector dropdown on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (selectorRef.current && !selectorRef.current.contains(e.target)) setVehicleOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const primaryId = selectedIds[0];
  const multi = selectedIds.length > 1;

  // ── Per-vehicle analytics (primary vehicle = 360° view) ──
  const primary = useMemo(() => {
    const pts = tracksByCar[primaryId] || [];
    const ts = computeTripsStops(pts);
    const car = cars.find(c => c.id === primaryId);
    const carViolations = mokhalafat.filter(m => m.carId === primaryId || m.vehicleId === primaryId).length;
    const behavior = driverBehavior(pts, { speedLimit: settings.gpsSpeedLimit || 120, violations: carViolations });
    return { pts, ...ts, car, behavior, speed: speedBuckets(pts), daily: dailySeries(ts.trips) };
  }, [tracksByCar, primaryId, cars, mokhalafat, settings]);

  const kpis = useMemo(() =>
    fleetKpis(cars, { breakdowns, vidanges, violations: mokhalafat }),
    [cars, breakdowns, vidanges, mokhalafat]);

  const status = useMemo(() => fleetStatus(cars), [cars]);
  const activity = useMemo(() => deviceActivity(cars), [cars]);

  // ── ApexCharts shared options ──
  const baseOpts = (extra = {}) => ({
    chart: { toolbar: { show: false }, background: 'transparent', fontFamily: 'inherit', animations: { enabled: true } },
    theme: { mode: isDark ? 'dark' : 'light' },
    grid: { borderColor: isDark ? '#334155' : '#e5e7eb', strokeDashArray: 4 },
    colors: PALETTE,
    dataLabels: { enabled: false },
    legend: { labels: { colors: isDark ? '#cbd5e1' : '#475569' } },
    tooltip: { theme: isDark ? 'dark' : 'light' },
    ...extra,
  });

  const cardBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';

  // ── Comparison datasets (multi-vehicle) ──
  const comparison = useMemo(() => selectedIds.slice(0, 6).map((id, i) => {
    const pts = tracksByCar[id] || [];
    const ts = computeTripsStops(pts);
    const car = cars.find(c => c.id === id);
    return { id, name: car ? carLabel(car) : id, color: PALETTE[i % PALETTE.length],
      km: +ts.totalKm.toFixed(1), trips: ts.trips.length, stops: ts.stops.length,
      maxSpeed: Math.round(ts.maxSpeed), movingH: +(ts.movingMs / 3600000).toFixed(1) };
  }), [selectedIds, tracksByCar, cars]);

  // ── Exports ──
  const buildSheets = () => {
    const kpiRows = [
      ['Indicateur', 'Valeur'],
      ['Véhicules', kpis.vehicles], ['En ligne', kpis.online], ['En mouvement', kpis.moving],
      ['En réparation', kpis.inRepair], ['Pannes ouvertes', kpis.openBreakdowns],
      ['Km total', kpis.totalKm], ['Km du jour', kpis.dailyKm], ['Alertes', kpis.alerts],
    ];
    const tripRows = [['Véhicule', 'Début', 'Fin', 'Distance (km)', 'Durée', 'Vitesse max']];
    selectedIds.forEach(id => {
      const car = cars.find(c => c.id === id); const label = car ? carLabel(car) : id;
      computeTripsStops(tracksByCar[id] || []).trips.forEach(tr => tripRows.push([
        label, new Date(tr.start).toLocaleString('fr-FR'), new Date(tr.end).toLocaleString('fr-FR'),
        +tr.distanceKm.toFixed(1), fmtDuration(tr.durationMs), Math.round(tr.maxSpeed),
      ]));
    });
    const stopRows = [['Véhicule', 'Début', 'Fin', 'Durée']];
    selectedIds.forEach(id => {
      const car = cars.find(c => c.id === id); const label = car ? carLabel(car) : id;
      computeTripsStops(tracksByCar[id] || []).stops.forEach(s => stopRows.push([
        label, new Date(s.start).toLocaleString('fr-FR'), new Date(s.end).toLocaleString('fr-FR'), fmtDuration(s.durationMs),
      ]));
    });
    const maintRows = [['Véhicule', 'Type', 'Date', 'Statut']];
    breakdowns.forEach(b => maintRows.push([b.carLabel || '', 'Panne', b.reportedAt ? new Date(b.reportedAt).toLocaleString('fr-FR') : '', b.status || '']));
    vidanges.forEach(v => maintRows.push([v.carName || v.carLabel || '', 'Vidange', v.date || v.createdAt || '', 'done']));
    return { kpiRows, tripRows, stopRows, maintRows };
  };

  const exportExcel = () => {
    const { kpiRows, tripRows, stopRows, maintRows } = buildSheets();
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(kpiRows), 'KPIs');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(tripRows), 'Trips');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(stopRows), 'Stops');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(maintRows), 'Maintenance');
    XLSX.writeFile(wb, `car_analytics_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const exportCsv = () => {
    const { tripRows } = buildSheets();
    const csv = tripRows.map(r => r.map(c => `"${String(c ?? '')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a'); a.href = url; a.download = `trips_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    if (!dashRef.current) return;
    html2pdf().set({
      margin: 6, filename: `car_analytics_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: 'jpeg', quality: 0.95 }, html2canvas: { scale: 2, backgroundColor: isDark ? '#0f172a' : '#ffffff' },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
    }).from(dashRef.current).save();
  };

  const toggleCar = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── KPI card ──
  const Kpi = ({ icon, label, value, accent }) => (
    <div className={`rounded-2xl border p-4 ${cardBg}`}>
      <div className="flex items-center justify-between">
        <span className="text-2xl">{icon}</span>
        <span className="text-2xl font-black" style={{ color: accent }}>{value}</span>
      </div>
      <div className={`text-[11px] font-bold mt-1 ${sub}`}>{label}</div>
    </div>
  );

  const Section = ({ title, icon, children, right }) => (
    <div className={`rounded-2xl border p-4 ${cardBg}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-black text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{icon} {title}</h3>
        {right}
      </div>
      {children}
    </div>
  );

  const NoData = ({ note }) => (
    <div className={`text-xs ${sub} py-6 text-center`}>📭 {note || t('Aucune donnée disponible pour cette période.', 'لا توجد بيانات لهذه الفترة.')}</div>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" dir={isAr ? 'rtl' : 'ltr'}>
      {/* Header + controls */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h1 className={`text-2xl font-black flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            📈 {t('Car Analytics', 'تحليلات السيارات')}
          </h1>
          <p className={`text-sm mt-1 ${sub}`}>
            {t('Vue 360° de votre flotte : trajets, arrêts, conduite, moteur, maintenance, alertes.',
               'رؤية 360° لأسطولك: الرحلات، التوقفات، القيادة، المحرك، الصيانة، التنبيهات.')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRangeKey(r.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold ${rangeKey === r.key ? 'bg-indigo-600 text-white' : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>
              {t(r.fr, r.ar)}
            </button>
          ))}
          <button onClick={() => setRangeKey('custom')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${rangeKey === 'custom' ? 'bg-indigo-600 text-white' : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>
            ⏱️ {t('Heure exacte', 'وقت محدد')}
          </button>
          <button onClick={() => setRealtime(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${realtime ? 'bg-green-600 text-white' : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>
            {realtime ? '🟢 Live' : '⚪ Live'}
          </button>
          <button onClick={exportPdf} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-50 text-red-700 border border-red-200">PDF</button>
          <button onClick={exportExcel} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-green-50 text-green-700 border border-green-200">Excel</button>
          <button onClick={exportCsv} className="px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">CSV</button>
        </div>
      </div>

      {/* Exact date+time range (shown when "Heure exacte" is selected) */}
      {rangeKey === 'custom' && (
        <div className={`rounded-2xl border p-3 mb-5 flex items-end gap-3 flex-wrap ${cardBg}`}>
          <div>
            <label className={`block text-[11px] font-bold mb-1 ${sub}`}>{t('Du (date + heure)', 'من (تاريخ + وقت)')}</label>
            <input type="datetime-local" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-800'}`} />
          </div>
          <div>
            <label className={`block text-[11px] font-bold mb-1 ${sub}`}>{t('Au (date + heure)', 'إلى (تاريخ + وقت)')}</label>
            <input type="datetime-local" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-800'}`} />
          </div>
          <div className={`text-[11px] ${sub} pb-2`}>
            {customFrom && customTo
              ? `${new Date(customFrom).toLocaleString(isAr ? 'ar-MA' : 'fr-FR')} → ${new Date(customTo).toLocaleString(isAr ? 'ar-MA' : 'fr-FR')}`
              : t('Choisissez la plage exacte', 'اختر المجال المحدد')}
          </div>
        </div>
      )}

      {/* Searchable vehicle selector — fixed/sticky, never resets on updates */}
      <div ref={selectorRef} className={`rounded-2xl border p-3 mb-5 sticky top-2 z-30 ${cardBg}`}>
        <div className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${sub}`}>
          {t('Véhicules sélectionnés', 'المركبات المختارة')} ({selectedIds.length})
        </div>

        {/* Selected chips */}
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {selectedIds.map(id => {
              const c = cars.find(x => x.id === id);
              return (
                <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-bold bg-indigo-600 text-white">
                  {c ? carLabel(c) : id}
                  <button onClick={() => toggleCar(id)} className="hover:text-indigo-200 font-black">✕</button>
                </span>
              );
            })}
            {multi && (
              <button onClick={() => setSelectedIds(primaryId ? [primaryId] : [])}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {t('Réinitialiser', 'إعادة')}
              </button>
            )}
          </div>
        )}

        {/* Searchable input */}
        <div className="relative">
          <input
            value={vehicleQuery}
            onChange={e => { setVehicleQuery(e.target.value); setVehicleOpen(true); }}
            onFocus={() => setVehicleOpen(true)}
            placeholder={t('🔍 Rechercher par matricule ou modèle…', '🔍 ابحث بالرقم أو الموديل…')}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-900 border-gray-600 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-800'}`}
          />
          {vehicleOpen && (
            <div className={`absolute left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-xl border shadow-xl z-40 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
              {(() => {
                const q = vehicleQuery.trim().toLowerCase();
                const list = (q
                  ? cars.filter(c => `${c.plate || ''} ${c.model || ''} ${c.brand || ''}`.toLowerCase().includes(q))
                  : cars
                ).slice(0, 60);
                if (list.length === 0) return <div className={`px-3 py-3 text-xs ${sub}`}>{t('Aucun résultat', 'لا نتائج')}</div>;
                return list.map(c => {
                  const on = selectedIds.includes(c.id);
                  return (
                    <button key={c.id} onClick={() => toggleCar(c.id)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition ${on ? 'bg-indigo-50 dark:bg-indigo-900/20' : (isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}`}>
                      <span className={isDark ? 'text-gray-200' : 'text-gray-800'}>
                        <span className="font-bold">{c.brand} {c.model}</span>
                        {c.plate && <span className={`ml-2 font-mono text-xs ${sub}`}>{c.plate}</span>}
                      </span>
                      <span className={`text-xs font-bold ${on ? 'text-indigo-600' : sub}`}>{on ? '✓' : '+'}</span>
                    </button>
                  );
                });
              })()}
            </div>
          )}
        </div>
        <div className={`text-[10px] mt-1.5 ${sub}`}>
          {t('Sélectionnez 1 véhicule (vue 360°) ou plusieurs pour comparer. Le choix reste fixe pendant les mises à jour.',
             'اختر مركبة واحدة (رؤية 360°) أو عدة للمقارنة. يبقى الاختيار ثابتاً أثناء التحديثات.')}
        </div>
      </div>

      <div ref={dashRef}>
        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
          <Kpi icon="🚗" label={t('Véhicules', 'المركبات')} value={kpis.vehicles} accent="#6366f1" />
          <Kpi icon="🟢" label={t('En ligne', 'متصل')} value={kpis.online} accent="#10b981" />
          <Kpi icon="🛣️" label={t('En mouvement', 'يتحرك')} value={kpis.moving} accent="#06b6d4" />
          <Kpi icon="🔧" label={t('En réparation', 'إصلاح')} value={kpis.inRepair} accent="#f59e0b" />
          <Kpi icon="🛠️" label={t('Pannes', 'أعطال')} value={kpis.openBreakdowns} accent="#ef4444" />
          <Kpi icon="📊" label={t('Km total', 'كم إجمالي')} value={kpis.totalKm.toLocaleString()} accent="#8b5cf6" />
          <Kpi icon="📅" label={t('Km aujourd\'hui', 'كم اليوم')} value={kpis.dailyKm} accent="#ec4899" />
          <Kpi icon="🔔" label={t('Alertes', 'تنبيهات')} value={kpis.alerts} accent="#ef4444" />
        </div>

        {/* Charts grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <div className="lg:col-span-2">
            <Section title={t('Distance par jour', 'المسافة يومياً')} icon="🛣️" right={loadingTracks && <span className="text-xs">⏳</span>}>
              {primary.daily.days.length ? (
                <ReactApexChart type="area" height={260}
                  series={[{ name: 'km', data: primary.daily.km }]}
                  options={baseOpts({ xaxis: { categories: primary.daily.days }, stroke: { curve: 'smooth', width: 2 }, fill: { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0.05 } } })} />
              ) : <NoData />}
            </Section>
          </div>
          <Section title={t('État de la flotte', 'حالة الأسطول')} icon="🚦">
            <ReactApexChart type="donut" height={260}
              series={[status.available, status.rented, status.maintenance, status.other]}
              options={baseOpts({ labels: [t('Disponible', 'متاح'), t('Louée', 'مؤجرة'), t('Réparation', 'إصلاح'), t('Autre', 'أخرى')], colors: ['#10b981', '#3b82f6', '#f59e0b', '#94a3b8'], legend: { position: 'bottom', labels: { colors: isDark ? '#cbd5e1' : '#475569' } } })} />
          </Section>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <Section title={t('Trajets par jour', 'الرحلات يومياً')} icon="📈">
            {primary.daily.days.length ? (
              <ReactApexChart type="bar" height={240}
                series={[{ name: t('Trajets', 'رحلات'), data: primary.daily.trips }]}
                options={baseOpts({ xaxis: { categories: primary.daily.days }, plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } } })} />
            ) : <NoData />}
          </Section>
          <Section title={t('Distribution de vitesse', 'توزيع السرعة')} icon="💨">
            {primary.pts.length ? (
              <ReactApexChart type="bar" height={240}
                series={[{ name: t('Points', 'نقاط'), data: primary.speed.counts }]}
                options={baseOpts({ xaxis: { categories: primary.speed.labels }, colors: ['#06b6d4'], plotOptions: { bar: { borderRadius: 4 } } })} />
            ) : <NoData />}
          </Section>
          <Section title={t('Activité des appareils', 'نشاط الأجهزة')} icon="📡">
            <ReactApexChart type="radialBar" height={240}
              series={[Math.round((activity.online / Math.max(1, cars.length)) * 100)]}
              options={baseOpts({ labels: [t('En ligne', 'متصل')], plotOptions: { radialBar: { hollow: { size: '55%' }, dataLabels: { value: { color: isDark ? '#fff' : '#111', formatter: () => `${activity.online}/${cars.length}` } } } } })} />
          </Section>
        </div>

        {/* Driver behavior + Engine + Vehicle health (primary) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <Section title={t('Comportement de conduite', 'سلوك القيادة')} icon="🧑‍✈️">
            {primary.pts.length ? (
              <div className="flex items-center gap-4">
                <ReactApexChart type="radialBar" height={180} width={160}
                  series={[primary.behavior.score]}
                  options={baseOpts({ labels: [t('Score', 'النقاط')], colors: [primary.behavior.score > 75 ? '#10b981' : primary.behavior.score > 50 ? '#f59e0b' : '#ef4444'], plotOptions: { radialBar: { hollow: { size: '55%' } } } })} />
                <div className="text-xs space-y-1">
                  <div>🚀 {t('Accél. brusques', 'تسارع حاد')}: <b>{primary.behavior.harshAccel}</b></div>
                  <div>🛑 {t('Freinages brusques', 'فرملة حادة')}: <b>{primary.behavior.harshBrake}</b></div>
                  <div>⚡ {t('Excès de vitesse', 'تجاوز السرعة')}: <b>{primary.behavior.speeding}</b></div>
                  <div>⚠️ {t('Infractions', 'مخالفات')}: <b>{primary.behavior.violations}</b></div>
                </div>
              </div>
            ) : <NoData />}
          </Section>
          <Section title={t('Moteur & Santé véhicule', 'المحرك وصحة المركبة')} icon="🔧">
            {primary.car ? (
              <div className="text-xs space-y-2">
                <Health label={t('Statut moteur', 'حالة المحرك')} value={(primary.car.gpsSpeed || 0) > 3 ? t('En marche', 'يعمل') : t('Arrêté', 'متوقف')} ok={(primary.car.gpsSpeed || 0) > 3} />
                <Health label={t('Appareil GPS', 'جهاز GPS')} value={primary.car.gpsDeviceStatus === 'online' ? 'Online' : 'Offline'} ok={primary.car.gpsDeviceStatus === 'online'} />
                <Health label={t('Odomètre', 'العداد')} value={`${Math.round(primary.car.gpsOdometer || 0)} km`} ok />
                <Health label={t('Vitesse actuelle', 'السرعة الحالية')} value={`${Math.round(primary.car.gpsSpeed || 0)} kph`} ok />
                <Health label={t('Dernier signal', 'آخر إشارة')} value={primary.car.gpsLastSeen ? new Date(primary.car.gpsLastSeen).toLocaleString(isAr ? 'ar-MA' : 'fr-FR') : '—'} ok={!!primary.car.gpsLastSeen} />
              </div>
            ) : <NoData />}
          </Section>
          <Section title={t('AdBlue', 'AdBlue')} icon="🧪">
            <div className={`text-xs ${sub} py-4 text-center`}>
              ℹ️ {t('Le niveau AdBlue n\'est pas transmis par les traceurs actuels. Module prêt — s\'activera dès qu\'un capteur AdBlue est disponible.',
                    'مستوى AdBlue غير مُرسَل من الأجهزة الحالية. الوحدة جاهزة وتُفعَّل عند توفر حساس AdBlue.')}
            </div>
          </Section>
        </div>

        {/* Comparison (multi-vehicle) */}
        {multi && (
          <div className="mb-4">
            <Section title={t('Comparaison multi-véhicules', 'مقارنة متعددة المركبات')} icon="⚖️">
              <ReactApexChart type="bar" height={300}
                series={[
                  { name: t('Distance (km)', 'مسافة'), data: comparison.map(c => c.km) },
                  { name: t('Trajets', 'رحلات'), data: comparison.map(c => c.trips) },
                  { name: t('Heures en route', 'ساعات السير'), data: comparison.map(c => c.movingH) },
                ]}
                options={baseOpts({ xaxis: { categories: comparison.map(c => c.name) }, plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } } })} />
            </Section>
          </div>
        )}

        {/* Detailed tables: Trip & Stop history */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <Section title={t('Historique des trajets', 'سجل الرحلات')} icon="🧭">
            <Table head={[t('Début', 'البداية'), t('Distance', 'المسافة'), t('Durée', 'المدة'), t('V.max', 'أقصى')]}
              rows={primary.trips.slice(0, 12).map(tr => [
                new Date(tr.start).toLocaleString(isAr ? 'ar-MA' : 'fr-FR'),
                `${tr.distanceKm.toFixed(1)} km`, fmtDuration(tr.durationMs, lang), `${Math.round(tr.maxSpeed)} kph`,
              ])} isDark={isDark} empty={t('Aucun trajet', 'لا رحلات')} />
          </Section>
          <Section title={t('Historique des arrêts', 'سجل التوقفات')} icon="🛑">
            <Table head={[t('Début', 'البداية'), t('Fin', 'النهاية'), t('Durée', 'المدة')]}
              rows={primary.stops.slice(0, 12).map(s => [
                new Date(s.start).toLocaleString(isAr ? 'ar-MA' : 'fr-FR'),
                new Date(s.end).toLocaleTimeString(isAr ? 'ar-MA' : 'fr-FR'), fmtDuration(s.durationMs, lang),
              ])} isDark={isDark} empty={t('Aucun arrêt', 'لا توقفات')} />
          </Section>
        </div>

        {/* Alerts & Events + Maintenance + Geofence */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <Section title={t('Alertes & Événements', 'التنبيهات والأحداث')} icon="🔔">
            <Table head={[t('Véhicule', 'مركبة'), t('Type', 'النوع'), t('Date', 'التاريخ')]}
              rows={[
                ...breakdowns.map(b => [b.carLabel || '', t('Panne', 'عطل'), b.reportedAt ? new Date(b.reportedAt).toLocaleDateString('fr-FR') : '']),
                ...mokhalafat.slice(0, 8).map(m => [m.carName || m.carLabel || '', t('Infraction', 'مخالفة'), m.date || '']),
              ].slice(0, 12)} isDark={isDark} empty={t('Aucune alerte', 'لا تنبيهات')} />
          </Section>
          <Section title={t('Maintenance', 'الصيانة')} icon="🧰">
            <Table head={[t('Véhicule', 'مركبة'), t('Type', 'النوع'), t('Date', 'التاريخ')]}
              rows={[
                ...vidanges.slice(0, 8).map(v => [v.carName || v.carLabel || '', t('Vidange', 'تغيير الزيت'), v.date || '']),
                ...breakdowns.filter(b => b.status === 'closed').slice(0, 6).map(b => [b.carLabel || '', t('Réparation', 'إصلاح'), b.closedAt ? new Date(b.closedAt).toLocaleDateString('fr-FR') : '']),
              ].slice(0, 12)} isDark={isDark} empty={t('Aucune maintenance', 'لا صيانة')} />
          </Section>
          <Section title={t('Geofence', 'السياج الجغرافي')} icon="🗺️">
            <div className={`text-xs ${sub} py-4 text-center`}>
              ℹ️ {t('Les changements de ville/zone sont notifiés via Telegram. L\'historique détaillé géofence s\'affichera ici dès activation du suivi de zones.',
                    'تغييرات المدينة/المنطقة تُرسل عبر Telegram. سيظهر سجل السياج الجغرافي هنا عند تفعيل تتبع المناطق.')}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

// ── small presentational helpers ──
function Health({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className={`font-bold ${ok ? 'text-green-600' : 'text-amber-600'}`}>{value}</span>
    </div>
  );
}

function Table({ head, rows, isDark, empty }) {
  if (!rows || rows.length === 0) {
    return <div className={`text-xs py-6 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>📭 {empty}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className={isDark ? 'text-gray-400' : 'text-gray-500'}>
            {head.map((h, i) => <th key={i} className="text-left font-bold py-1.5 px-2">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={`border-t ${isDark ? 'border-gray-700 text-gray-200' : 'border-gray-100 text-gray-700'}`}>
              {r.map((c, j) => <td key={j} className="py-1.5 px-2 whitespace-nowrap">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
